import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { QRCode, Spin } from "antd";
import {
  getRechargePackagesApi,
  type PointRechargePackageRespVO,
} from "../../service/recharge";
import {
  createAppPaymentOrderApi,
  isAppPaymentSuccess,
  queryAppPaymentStatusApi,
  type PayApiChannel,
} from "../../service/pay";
import { triggerMembershipRefresh } from "../../service/membership";
import { globalAlert } from "../../utils/globalAlert";
import { globalStore } from "../../utils/globalStore";
import { LEGAL_DOC_HREFS } from "../../legal/legalDocRoutes";
import "./pointsPurchaseModal.scss";

export interface PointsPurchaseModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * 当前用户是否为「基础会员」——prop 形式作为 fallback。
   * - 默认 true
   */
  isBasicMember?: boolean;
  /** 支付成功（轮询到 success）后回调 */
  onPaySuccess?: () => void;
}

interface PurchasePack {
  id: number;
  amount: string;
  amountValue: number;
  points: number;
  bonus: number;
  desc: string;
  tag: string;
  recommended: boolean;
}

type PayMethod = "wechat" | "alipay";

function formatYuan(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return `¥${n.toLocaleString("zh-CN")}`;
}

function formatPoints(value: number): string {
  const n = Number.isFinite(value) ? Math.trunc(value) : 0;
  return n.toLocaleString("zh-CN");
}

function computeBonusPercent(bonusPoint: number, basePoint: number): number {
  if (!basePoint || basePoint <= 0) return 0;
  return Math.round((bonusPoint / basePoint) * 100);
}

function mapPackagesToPacks(pkgs: PointRechargePackageRespVO[]): PurchasePack[] {
  const enabled = pkgs.filter((p) => p.status === 1);
  const sorted = [...enabled].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  if (sorted.length === 0) return [];
  const maxTotal = sorted.reduce(
    (acc, p) => Math.max(acc, p.totalPoint || 0),
    0
  );
  return sorted.map((p, idx) => {
    const isRecommended =
      sorted.length > 1 && (p.totalPoint || 0) === maxTotal;
    const bonusPercent = computeBonusPercent(p.bonusPoint || 0, p.basePoint || 0);
    const tag = bonusPercent > 0
      ? isRecommended
        ? `最多赠送 · ${bonusPercent}%`
        : `送 ${bonusPercent}%`
      : "";
    const pointsText = `${formatPoints(p.totalPoint || 0)} 积分`;
    const desc = idx === 0 ? `${pointsText} · 应急补充` : pointsText;
    return {
      id: p.id,
      amount: formatYuan(p.priceYuan),
      amountValue: p.priceYuan,
      points: p.totalPoint || 0,
      bonus: p.bonusPoint || 0,
      desc,
      tag,
      recommended: isRecommended,
    };
  });
}

function uiChannelToApi(ch: PayMethod): PayApiChannel {
  return ch === "wechat" ? "wechat_native" : "alipay_qrcode";
}

function resolveUserId(): number | null {
  const u = (globalStore.getUserInfo() as { id?: number } | null)?.id;
  return typeof u === "number" && u > 0 ? u : null;
}

/**
 * 积分充值弹窗：
 * - 左侧套餐：拉取 GET /points/recharge-package/page
 * - 支付流程：POST /pay/create（按 packageId 下单）+ GET /pay/query 轮询
 * - 推荐档 = totalPoint 最大（前端推断）
 */
export const PointsPurchaseModal: FC<PointsPurchaseModalProps> = ({
  open,
  onClose,
  isBasicMember: isBasicMemberFallback = true,
  onPaySuccess,
}) => {
  const navigate = useNavigate();
  const handleComparePlans = useCallback(() => {
    onClose();
    navigate("/pricing");
  }, [navigate, onClose]);

  // 套餐
  const [packs, setPacks] = useState<PurchasePack[]>([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsError, setPacksError] = useState<string | null>(null);
  const [activePackId, setActivePackId] = useState<number | null>(null);

  // 支付
  const [activePay, setActivePay] = useState<PayMethod>("alipay");
  const [codeUrl, setCodeUrl] = useState<string | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [qrExpired, setQrExpired] = useState(false);
  const [rechargeNo, setRechargeNo] = useState<string | null>(null);

  const paySessionRef = useRef(0);
  const pollIntervalRef = useRef<number | null>(null);
  const expiryTimerRef = useRef<number | null>(null);

  const clearPayTimers = useCallback(() => {
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (expiryTimerRef.current != null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const stopPollingOnly = useCallback(() => {
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // 拉取套餐
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPacksLoading(true);
    setPacksError(null);
    getRechargePackagesApi({ pageNo: 1, pageSize: 100, status: 1 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.list) ? res.data.list : [];
        const mapped = mapPackagesToPacks(list);
        setPacks(mapped);
        setActivePackId(mapped[0]?.id ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setPacks([]);
        setPacksError("套餐加载失败，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setPacksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // 单次轮询
  const runPollOnce = useCallback(
    async (no: string, sid: number) => {
      if (sid !== paySessionRef.current) return;
      try {
        const res = await queryAppPaymentStatusApi(no);
        if (sid !== paySessionRef.current) return;
        const status = res?.data?.paymentStatus;
        if (isAppPaymentSuccess(status)) {
          clearPayTimers();
          setPayLoading(false);
          setPayError(null);
          globalAlert.showSuccess("支付成功");
          window.dispatchEvent(new Event("points:refresh"));
          triggerMembershipRefresh();
          onPaySuccess?.();
          onClose();
          return;
        }
        if (status === "failed" || status === "closed") {
          stopPollingOnly();
          setPayLoading(false);
          setPayError("支付未成功，请重试或更换支付方式。");
          return;
        }
      } catch {
        /* 单次失败忽略，下次继续轮询 */
      }
    },
    [clearPayTimers, stopPollingOnly, onClose, onPaySuccess]
  );

  // 启动下单 + 轮询
  const bootstrapPay = useCallback(async () => {
    const uid = resolveUserId();
    if (uid == null) {
      setPayError("请先登录后再充值");
      setCodeUrl(null);
      setQrCodeImage(null);
      setRechargeNo(null);
      setPayLoading(false);
      clearPayTimers();
      return;
    }

    const currentPackId = activePackIdRef.current;
    if (currentPackId == null) return;

    clearPayTimers();
    paySessionRef.current += 1;
    const sid = paySessionRef.current;

    setPayLoading(true);
    setPayError(null);
    setQrExpired(false);
    setCodeUrl(null);
    setQrCodeImage(null);
    setRechargeNo(null);

    try {
      const res = await createAppPaymentOrderApi({
        packageId: currentPackId,
        payChannel: uiChannelToApi(activePayRef.current),
        userId: uid,
      });
      if (sid !== paySessionRef.current) return;

      if (res.code !== 0 || !res.data?.rechargeNo) {
        const msg = res.msg || "创建支付订单失败";
        setPayError(msg);
        return;
      }

      setCodeUrl(res.data.codeUrl || null);
      setQrCodeImage(res.data.qrCodeImage || null);
      const no = res.data.rechargeNo;
      setRechargeNo(no);

      void runPollOnce(no, sid);
      pollIntervalRef.current = window.setInterval(() => {
        void runPollOnce(no, sid);
      }, 2000);
      expiryTimerRef.current = window.setTimeout(() => {
        if (sid !== paySessionRef.current) return;
        setQrExpired(true);
        stopPollingOnly();
      }, 60_000);
    } catch (e: unknown) {
      if (sid !== paySessionRef.current) return;
      const msg =
        typeof e === "object" && e !== null && "message" in e && typeof (e as Error).message === "string"
          ? (e as Error).message
          : "创建支付订单失败";
      setPayError(msg);
    } finally {
      if (sid === paySessionRef.current) {
        setPayLoading(false);
      }
    }
  }, [clearPayTimers, stopPollingOnly, runPollOnce]);

  // 同步当前选中 / 支付方式到 ref，供 bootstrapPay 内部读取
  const activePackIdRef = useRef<number | null>(null);
  const activePayRef = useRef<PayMethod>("alipay");
  useEffect(() => {
    activePackIdRef.current = activePackId;
  }, [activePackId]);
  useEffect(() => {
    activePayRef.current = activePay;
  }, [activePay]);

  // 打开弹窗且默认套餐就绪 → 自动下单
  const bootstrappedForPackRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      bootstrappedForPackRef.current = null;
      return;
    }
    if (packsLoading) return;
    if (activePackId == null) return;
    if (bootstrappedForPackRef.current === activePackId) return;
    bootstrappedForPackRef.current = activePackId;
    void bootstrapPay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packsLoading, activePackId]);

  // 切换支付方式 → 重新下单
  const bootstrappedForPayRef = useRef<PayMethod | null>(null);
  useEffect(() => {
    if (!open) {
      bootstrappedForPayRef.current = null;
      return;
    }
    if (activePackId == null) return;
    if (bootstrappedForPayRef.current === activePay) return;
    bootstrappedForPayRef.current = activePay;
    void bootstrapPay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activePay]);

  // 用户主动切套餐 → 重新下单
  const handleSelectPack = useCallback(
    (id: number) => {
      setActivePackId(id);
      bootstrappedForPackRef.current = null; // 强制重下
    },
    []
  );

  // 卸载清理
  useEffect(() => {
    return () => {
      clearPayTimers();
      paySessionRef.current += 1;
    };
  }, [clearPayTimers]);

  // 关闭时清理
  useEffect(() => {
    if (!open) {
      clearPayTimers();
      paySessionRef.current += 1;
      setCodeUrl(null);
      setQrCodeImage(null);
      setPayLoading(false);
      setPayError(null);
      setQrExpired(false);
      setRechargeNo(null);
      bootstrappedForPackRef.current = null;
      bootstrappedForPayRef.current = null;
    }
  }, [open, clearPayTimers]);

  const handleRefreshQr = useCallback(() => {
    bootstrappedForPackRef.current = null;
    bootstrappedForPayRef.current = null;
    setActivePackId((cur) => cur); // 触发重新 bootstrap
    void bootstrapPay();
  }, [bootstrapPay]);

  const currentPack =
    packs.find((p) => p.id === activePackId) ?? null;
  const showUpgradeNudge =
    isBasicMemberFallback && currentPack !== null && currentPack.amountValue >= 200;

  if (!open) return null;

  const hasQr = !!(codeUrl || qrCodeImage);

  return (
    <section className="points-purchase-modal" role="dialog" aria-modal="true" aria-label="积分充值">
      <div className="points-purchase-modal__body">
        <div className="points-purchase-modal__pack-column">
          {packsError ? (
            <div className="points-purchase-modal__error" role="alert">
              {packsError}
            </div>
          ) : null}
          <div className="points-purchase-modal__pack-grid">
            {packsLoading ? (
              <div className="points-purchase-modal__loading">
                <Spin size="small" /> <span>套餐加载中…</span>
              </div>
            ) : packs.length === 0 ? (
              <div className="points-purchase-modal__loading">
                <span>暂无套餐</span>
              </div>
            ) : (
              packs.map((pack) => {
                const classes = [
                  "points-purchase-modal__pack",
                  activePackId === pack.id ? "is-selected" : "",
                  pack.recommended ? "is-recommended" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    key={pack.id}
                    type="button"
                    className={classes}
                    onClick={() => handleSelectPack(pack.id)}
                  >
                    {pack.tag ? <em>{pack.tag}</em> : null}
                    <strong>{pack.amount}</strong>
                    <span className="points-purchase-modal__pack-line">
                      {pack.desc}
                      {pack.bonus > 0 ? (
                        <small className="points-purchase-modal__pack-bonus">
                          +{formatPoints(pack.bonus)} 赠送
                        </small>
                      ) : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {showUpgradeNudge ? (
            <div className="points-purchase-modal__upgrade-nudge">
              <div>
                <strong>升级可能更划算</strong>
                <span>高级会员 ¥199/月，每月包含 3,500 积分</span>
              </div>
              <a
                href="/pricing"
                onClick={(e) => {
                  e.preventDefault();
                  handleComparePlans();
                }}
              >
                对比会员
              </a>
            </div>
          ) : null}
        </div>
        <div className="points-purchase-modal__checkout-column">
          <div className="enterprise-vip-recharge__pay-tabs" role="tablist" aria-label="支付方式">
            <button
              type="button"
              role="tab"
              aria-selected={activePay === "wechat"}
              className={`pay-method-btn enterprise-vip-recharge__pay-tab${activePay === "wechat" ? " pay-selected" : ""}`}
              onClick={() => setActivePay("wechat")}
            >
              <span className="enterprise-vip-recharge__pay-mark">微</span>
              <span>微信支付</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activePay === "alipay"}
              className={`pay-method-btn enterprise-vip-recharge__pay-tab${activePay === "alipay" ? " pay-selected" : ""}`}
              onClick={() => setActivePay("alipay")}
            >
              <span className="enterprise-vip-recharge__pay-mark">支</span>
              <span>支付宝</span>
            </button>
          </div>
          <div className="points-purchase-modal__summary">
            <p>订单摘要</p>
            <div>
              <span>充值金额</span>
              <strong>{currentPack?.amount ?? "—"}</strong>
            </div>
            <div className="points-purchase-modal__summary-total">
              <span>合计积分</span>
              <strong>
                {currentPack ? formatPoints(currentPack.points) : "—"}
              </strong>
            </div>
          </div>

          <div
            className={`points-purchase-modal__qr${qrExpired && hasQr ? " is-expired" : ""}`}
          >
            {payError && !payLoading ? (
              <div className="points-purchase-modal__qr-state points-purchase-modal__qr-state--error">
                <p className="points-purchase-modal__qr-state-text">{payError}</p>
                <button
                  type="button"
                  className="points-purchase-modal__qr-retry"
                  onClick={handleRefreshQr}
                >
                  重试
                </button>
              </div>
            ) : null}

            {!payError && payLoading && !hasQr ? (
              <div className="points-purchase-modal__qr-state">
                <Spin />
                <p className="points-purchase-modal__qr-state-hint">
                  正在获取支付二维码…
                </p>
              </div>
            ) : null}

            {!payError && hasQr ? (
              <div className="points-purchase-modal__qr-code-box">
                {qrCodeImage ? (
                  <img
                    src={qrCodeImage}
                    alt="支付二维码"
                    className="points-purchase-modal__qr-img"
                  />
                ) : codeUrl ? (
                  <QRCode value={codeUrl} size={112} color="#222B38" bordered={false} />
                ) : null}
              </div>
            ) : null}

            {!payError && !payLoading && !hasQr ? (
              <span className="points-purchase-modal__qr-placeholder">请选择套餐</span>
            ) : null}

            {qrExpired && hasQr && !payError ? (
              <button
                type="button"
                className="points-purchase-modal__qr-expired-mask"
                onClick={handleRefreshQr}
              >
                <ReloadOutlined className="points-purchase-modal__qr-expired-icon" />
                <span>二维码已过期，点击刷新</span>
              </button>
            ) : null}
          </div>

          <p className="points-purchase-modal__instruction">
            {activePay === "wechat" ? "请使用微信扫码支付" : "请使用支付宝扫码支付"}
          </p>
          <p className="points-purchase-modal__terms">
            支付即代表您已阅读并同意
            <br />
            <a
              href={LEGAL_DOC_HREFS.userAgreement}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              《用户协议》
            </a>
            <a
              href={LEGAL_DOC_HREFS.privacyPolicy}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              《隐私政策》
            </a>
            <a
              href={LEGAL_DOC_HREFS.subscriptionAgreement}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              《订购服务协议》
            </a>
          </p>
        </div>
      </div>
      <div className="points-purchase-modal__foot">
        温馨提示：积分不可转赠和提现；积分包自购买日起永久有效，不支持退款。
      </div>
    </section>
  );
};